import { diContainer } from '../libs/container';
import { ApiBaseClass, IServiceHttp } from './ApiBase';
import {
    AbsenceByPolicyDto,
    AbsenceReportScopeDto,
    EmployeeLeaveBalanceDto,
    ManagedProjectDto,
    MonthlyDetailDayDto,
    MonthlyTrendMonthDto,
    QuarterlyTrendDto,
    ReportsPage,
    TopEmployeesByRemainingDaysDto,
    UpcomingAbsenceDto,
    WeeklyTrendDayDto,
    WeeklyTrendDayOfWeek,
} from '../commonTypes/reports.types';

export type TrendDirection = 'up' | 'down' | 'neutral';

export interface TrendDto {
    value: number;
    direction: TrendDirection;
}

export namespace AbsenceReportApiRequest {
    export interface EmptyRequest {}

    export interface GetEmployeeLeaveBalance {
        query: {
            leaveType: string;
            search?: string;
            projectId?: string;
            page: number;
            size: number;
        };
    }

    export interface GetTopEmployeeLeaveBalance {
        query: {
            leaveType: string;
            teamId?: string;
            projectId?: string;
            search?: string;
            sort: 'remainingDesc' | 'remainingAsc';
            page: number;
            size: number;
        };
    }

    export interface GetUpcomingAbsences {
        query: {
            month: number;
            year: number;
            teamId?: string;
            projectId?: string;
            leaveType?: string;
            search?: string;
            page: number;
            size: number;
        };
    }

    export interface GetPoliciesBreakdown {
        query: {
            month: number;
            year: number;
        };
    }

    export interface GetWeeklyTrend {
        query: {
            fromDate: string;
            toDate: string;
            projectId?: string;
        };
    }

    export interface GetQuarterlyTrend {
        query: {
            year: number;
            projectId?: string;
        };
    }

    export interface GetMonthlyDetail {
        query: {
            month: number;
            year: number;
            projectId?: string;
        };
    }
}

export namespace AbsenceReportApiResponse {
    export type GetOverview =
        | {
              body: {
                  dataAvailable: true;
                  totalAbsences: number;
                  totalAbsencesTrend: TrendDto;
                  onLeaveToday: number;
                  onLeaveTodayTrend: TrendDto;
                  totalEmployees: number;
              };
          }
        | {
              body: {
                  dataAvailable: false;
              };
          };

    export type GetMonthlyTrend =
        | {
              body: {
                  dataAvailable: true;
                  months: MonthlyTrendMonthDto[];
              };
          }
        | {
              body: {
                  dataAvailable: false;
              };
          };

    export interface GetEmployeeLeaveBalance {
        body: ReportsPage<EmployeeLeaveBalanceDto>;
    }

    export interface GetTopEmployeeLeaveBalance {
        body: ReportsPage<TopEmployeesByRemainingDaysDto>;
    }

    export interface GetUpcomingAbsences {
        body: ReportsPage<UpcomingAbsenceDto>;
    }

    export interface GetPoliciesBreakdown {
        body: AbsenceByPolicyDto[];
    }

    export interface GetWeeklyTrend {
        body: {
            days: WeeklyTrendDayDto[];
            totalAbsent: number;
            busiestDay: WeeklyTrendDayOfWeek | 'ALL' | null;
            busiestDayCount: number;
            busiestDayTie: boolean;
            managedProjects?: ManagedProjectDto[];
        };
    }

    export interface GetQuarterlyTrend {
        body: {
            scope: AbsenceReportScopeDto;
            quarters: QuarterlyTrendDto[];
        };
    }

    export interface GetMonthlyDetail {
        body: {
            scope: AbsenceReportScopeDto;
            days: MonthlyDetailDayDto[];
        };
    }
}

export class AbsenceReportApi {
    constructor(private readonly apiBase: ApiBaseClass) {}

    public getOverview(rest: IServiceHttp<AbsenceReportApiRequest.EmptyRequest, AbsenceReportApiResponse.GetOverview>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/overview',
        });
    }

    public triggerCompute(rest: IServiceHttp<AbsenceReportApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/reports/absence/compute',
        });
    }

    public getMonthlyTrend(rest: IServiceHttp<AbsenceReportApiRequest.EmptyRequest, AbsenceReportApiResponse.GetMonthlyTrend>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/monthly-trend',
        });
    }

    public getEmployeeLeaveBalance(
        rest: IServiceHttp<AbsenceReportApiRequest.GetEmployeeLeaveBalance, AbsenceReportApiResponse.GetEmployeeLeaveBalance>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/leave-balance',
        });
    }

    public getTopEmployeeLeaveBalance(
        rest: IServiceHttp<AbsenceReportApiRequest.GetTopEmployeeLeaveBalance, AbsenceReportApiResponse.GetTopEmployeeLeaveBalance>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/leave-balance/top',
        });
    }

    public getUpcomingAbsences(rest: IServiceHttp<AbsenceReportApiRequest.GetUpcomingAbsences, AbsenceReportApiResponse.GetUpcomingAbsences>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/upcoming',
        });
    }

    public getPoliciesBreakdown(rest: IServiceHttp<AbsenceReportApiRequest.GetPoliciesBreakdown, AbsenceReportApiResponse.GetPoliciesBreakdown>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/policies-breakdown',
        });
    }

    public getWeeklyTrend(rest: IServiceHttp<AbsenceReportApiRequest.GetWeeklyTrend, AbsenceReportApiResponse.GetWeeklyTrend>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/trend/weekly',
        });
    }

    public getQuarterlyTrend(rest: IServiceHttp<AbsenceReportApiRequest.GetQuarterlyTrend, AbsenceReportApiResponse.GetQuarterlyTrend>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/trend/quarterly',
        });
    }

    public getMonthlyDetail(rest: IServiceHttp<AbsenceReportApiRequest.GetMonthlyDetail, AbsenceReportApiResponse.GetMonthlyDetail>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/reports/absence/trend/monthly-detail',
        });
    }
}

diContainer.bindAsDynamic(AbsenceReportApi, c => new AbsenceReportApi(c.get(ApiBaseClass)));
